import { Ray } from './Ray'
import type { Matrix4Like, QuaternionLike, Vector3Like } from './types'

/**
 * What picking and the interactions read from the viewport's camera: its matrices and where it is.
 * The camera itself is the renderer's (a three camera satisfies this as it is); product code never
 * needs more of it.
 */
export type CameraView = {
  readonly position: Vector3Like
  readonly quaternion: QuaternionLike
  readonly matrixWorld: Matrix4Like
  readonly matrixWorldInverse: Matrix4Like
  readonly projectionMatrix: Matrix4Like
  readonly projectionMatrixInverse: Matrix4Like
  /** Read for orthographic cameras only (three's perspective and orthographic cameras have them). */
  readonly near?: number
  readonly far?: number
  /** Perspective cameras: vertical field of view in degrees. */
  readonly fov?: number
  /** Orthographic cameras: the frustum's top and bottom. */
  readonly top?: number
  readonly bottom?: number
  readonly isPerspectiveCamera?: boolean
  readonly isOrthographicCamera?: boolean
}

/**
 * The ray through normalized device coordinates (`ndcX`, `ndcY`) of `camera` (three's
 * `Raycaster.setFromCamera`): from the camera's position for a perspective camera, from the near
 * plane straight ahead for an orthographic one.
 */
export function rayFromCamera(ndcX: number, ndcY: number, camera: CameraView, target = new Ray()): Ray {
  if (camera.isOrthographicCamera) {
    const near = camera.near ?? 0, far = camera.far ?? 0
    target.origin.set(ndcX, ndcY, (near + far) / (near - far)).unproject(camera)
    target.direction.set(0, 0, -1).transformDirection(camera.matrixWorld)
  } else {
    target.origin.setFromMatrixPosition(camera.matrixWorld)
    target.direction.set(ndcX, ndcY, 0.5).unproject(camera).sub(target.origin).normalize()
  }
  return target
}

/** Normalized device coordinates of a client point over `rect` (y up). */
export function clientToNdc(clientX: number, clientY: number, rect: { left: number; top: number; width: number; height: number }): { x: number; y: number } {
  return {
    x: ((clientX - rect.left) / rect.width) * 2 - 1,
    y: -(((clientY - rect.top) / rect.height) * 2 - 1),
  }
}
